import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { scrollToId } from "@/lib/lenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1497015289639-54688650d173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxmaWxtJTIwc2V0JTIwcHJvZHVjdGlvbiUyMGNhbWVyYXxlbnwwfHx8fDE3ODc3NDA3OTN8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Cinema camera on a film set"
          className="h-[115%] w-full scale-105 object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-[#050505]" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 70% 30%, rgba(183,144,69,0.14), transparent 55%)",
          }}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute right-6 top-24 md:right-12 md:top-28"
      >
        <motion.img
          src="/assets/veritas-seal.png"
          alt=""
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          className="h-24 w-24 rounded-full mix-blend-lighten md:h-36 md:w-36"
        />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto w-full max-w-[1440px] px-5 pb-10 pt-28 md:px-10 md:pb-12"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] md:text-xs"
        >
          <span className="h-px w-10 bg-[#B79045]" />
          Integrated Commercial, Audit &amp; Accounting Management
        </motion.p>

        <h1 className="font-display text-[clamp(2.6rem,5.9vw,6.6rem)] font-medium leading-[0.96] tracking-tight">
          <MaskedLine delay={0.35}>One Project.</MaskedLine>
          <MaskedLine delay={0.5}>
            One Connected <em className="italic text-[#D4AF37]">Financial</em>
          </MaskedLine>
          <MaskedLine delay={0.65}>
            <em className="italic text-[#D4AF37]">System.</em> One Point of Control.
          </MaskedLine>
        </h1>

        <div className="mt-8 flex flex-col gap-8 md:mt-10 md:flex-row md:items-end md:justify-between">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="max-w-xl text-sm leading-relaxed text-neutral-400 md:text-base"
          >
            From first budget to final cost, we connect the numbers behind Film,
            OTT, Television, Commercial and Media Productions — so producers can
            focus on what they do best: bringing stories to life.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-services-button"
              onClick={() => scrollToId("#services")}
              className="group inline-flex items-center gap-3 bg-[#B79045] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#D4AF37]"
            >
              Our Services
              <ArrowDown
                size={14}
                className="transition-transform duration-300 group-hover:translate-y-0.5"
              />
            </button>
            <button
              data-testid="hero-talk-button"
              onClick={() => scrollToId("#contact")}
              className="inline-flex items-center gap-3 border border-white/25 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-200 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              Talk to Us <ArrowUpRight size={14} />
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="relative border-t border-white/10"
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 md:px-10">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Veritas Advisory LLP
          </span>
          <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 md:block">
            Transparency in Numbers. Trust in Every Frame.
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
            Film · OTT · TV
          </span>
        </div>
      </motion.div>
    </section>
  );
}
